import { requestCommissionUpdate } from "@faire/help-center-self-serve/brand/instantAnswers/orders-requestCommissionUpdate";
import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Truck } from "@faire/web--source/slate/icons/foundation/Truck";
import React from "react";

import { OTHER_NODES_LABEL } from "../../shared/other-node";
import { SelfServeNodes } from "../../types";
import { GeneralContactRetailerIA } from "../instantAnswers/shared-generalContactRetailer";
import { AddOrUpdate } from "../instantAnswers/shipping-addOrUpdate";
import { AddressAndPhoneNumber } from "../instantAnswers/shipping-addressAndPhoneNumber";
import { BrandResponsibility } from "../instantAnswers/shipping-brandResponsibility";
import { CarrierNotAvailable } from "../instantAnswers/shipping-carrierNotAvailable";
import { Customs } from "../instantAnswers/shipping-customs";
import { HowShippingWorks } from "../instantAnswers/shipping-howShippingWorks";
import { PackageDimensionsAndWeight } from "../instantAnswers/shipping-packageDimensionsAndWeight";
import { Reimbursement } from "../instantAnswers/shipping-reimbursement";
import { SwfAvailability } from "../instantAnswers/shipping-swfAvailability";

export const useShippingBrandNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    Shipping: {
      id: "Shipping",
      label: strictLocalize({
        defaultMessage: "Shipping",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <Truck fontSize="24px" color="faireBlack" />,
      children: [
        "Swf",
        "SOYO",
        "GeneralShippingIssues",
        "commissionChangeRequest",
        "OtherShippingL2",
      ],
      level: 1,
      sidebarTitle: strictLocalize({
        defaultMessage: "Shipping issue",
        description: {
          text: "Title for displaying the issue on the order summary sidebar",
        },
      }),
    },
    commissionChangeRequest: {
      id: "commissionChangeRequest",
      label: strictLocalize({
        defaultMessage: "Request commission change",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center. It's under orders and shipping topic. It's for how to request commission change on a specific order.",
          img: "https://cdn.faire.com/fastly/296275c1942c036992d1be3e2bfdca9e253fbc663ca215e83c2647f1b9fddd24.png",
        },
      }),
      level: 2,
      instantAnswer: requestCommissionUpdate(strictLocalize, articles),
    },
    Swf: {
      id: "Swf",
      label: strictLocalize({
        defaultMessage: "Ship with Faire",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "Availability",
        "ShipmentTrackingSWF",
        "ShippingLabels",
        "InternationalOrdersSWF",
        "DamagedOrMissing",
        "Insurance",
        "CarrierPickup",
        "OtherSWF",
      ],
      level: 2,
    },
    SOYO: {
      id: "SOYO",
      label: strictLocalize({
        defaultMessage: "Ship on your own",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "ShipmentTrackingSOYO",
        "Reimbursement",
        "InternationalOrdersSOYO",
        "HowShippingWorks",
        "OtherSOYO",
      ],
      level: 2,
    },
    GeneralShippingIssues: {
      id: "GeneralShippingIssues",
      label: strictLocalize({
        defaultMessage: "General shipping issues",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["HowShippingWorks"],
      level: 2,
    },
    OtherShippingL2: {
      id: "OtherShippingL2",
      label: strictLocalize({
        defaultMessage: "Other",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.FulfillingOrders.ACCEPTING_ORDER,
        articles.FulfillingOrders.FULFILLING_ORDER,
        articles.FulfillingOrders.SHIPPING_ORDER,
        articles.FulfillingOrders.SETTING_OUT_OF_STOCK,
      ],
      level: 2,
    },
    Availability: {
      id: "Availability",
      label: strictLocalize({
        defaultMessage: "Availability",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["SwfAvailability", "OtherBrandShippingSWFL4"],
      level: 3,
    },
    ShipmentTrackingSWF: {
      id: "ShipmentTrackingSWF",
      label: strictLocalize({
        defaultMessage: "Shipment tracking",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["CannotTrackShipment"],
      level: 3,
    },
    ShippingLabels: {
      id: "ShippingLabels",
      label: strictLocalize({
        defaultMessage: "Label issues",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "AddressAndPhoneNumber",
        "PackageDimensionsAndWeight",
        "CarrierNotAvailable",
      ],
      level: 3,
    },
    InternationalOrdersSWF: {
      id: "InternationalOrdersSWF",
      label: strictLocalize({
        defaultMessage: "Issues with international shipping",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "BrandResponsibility",
        "Commercial",
        "Customs",
        "RefusedPackage",
        "ShippingUKandEU",
        "ShippingCAN",
        "ShippingUS",
        "OtherInternationalOrdersSWF",
      ],
      level: 3,
    },
    DamagedOrMissing: {
      id: "DamagedOrMissing",
      label: strictLocalize({
        defaultMessage: "Damaged or missing shipments",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    Insurance: {
      id: "Insurance",
      label: strictLocalize({
        defaultMessage: "Insurance with Ship with Faire",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    CarrierPickup: {
      id: "CarrierPickup",
      label: strictLocalize({
        defaultMessage: "Carrier pick-up",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    OtherSWF: {
      id: "OtherSWF",
      label: OTHER_NODES_LABEL(strictLocalize),
      level: 3,
    },
    ShipmentTrackingSOYO: {
      id: "ShipmentTrackingSOYO",
      label: strictLocalize({
        defaultMessage: "Shipment tracking",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["AddOrUpdate", "OtherShipmentTrackingSOYO"],
      level: 3,
    },
    Reimbursement: {
      id: "Reimbursement",
      label: strictLocalize({
        defaultMessage: "Reimbursement of shipping costs",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FulfillingOrders.LABEL_ISSUES],
      instantAnswer: Reimbursement(strictLocalize),
      level: 3,
    },
    InternationalOrdersSOYO: {
      id: "InternationalOrdersSOYO",
      label: strictLocalize({
        defaultMessage: "Issues with international shipping",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "BrandResponsibility",
        "Commercial",
        "Customs",
        "RefusedPackage",
        "ShippingUKandEU",
        "ShippingCAN",
        "ShippingUS",
        "OtherInternationalOrdersSOYO",
      ],
      level: 3,
    },
    HowShippingWorks: {
      id: "HowShippingWorks",
      label: strictLocalize({
        defaultMessage: "How does shipping work",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: HowShippingWorks(strictLocalize),
      articles: [
        articles.FulfillingOrders.SHIPPING_ORDER,
        articles.International.SELLING,
      ],
      level: 3,
    },
    OtherSOYO: {
      id: "OtherSOYO",
      label: OTHER_NODES_LABEL(strictLocalize),
      level: 3,
    },
    SwfAvailability: {
      id: "SwfAvailability",
      label: strictLocalize({
        defaultMessage: "Is Ship with Faire available in my country",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: SwfAvailability(strictLocalize),
      articles: [articles.FulfillingOrders.SHIP_WITH_FAIRE],
      level: 4,
    },
    CannotTrackShipment: {
      id: "CannotTrackShipment",
      label: strictLocalize({
        defaultMessage: "Cannot track shipment using SWF label",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 4,
    },
    AddressAndPhoneNumber: {
      id: "AddressAndPhoneNumber",
      label: strictLocalize({
        defaultMessage: "Address and phone number",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: AddressAndPhoneNumber(strictLocalize),
      articles: [articles.FulfillingOrders.LABEL_ISSUES],
      level: 4,
    },
    PackageDimensionsAndWeight: {
      id: "PackageDimensionsAndWeight",
      label: strictLocalize({
        defaultMessage: "Package dimensions and weight",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: PackageDimensionsAndWeight(strictLocalize),
      articles: [articles.FulfillingOrders.LABEL_ISSUES],
      level: 4,
    },
    CarrierNotAvailable: {
      id: "CarrierNotAvailable",
      label: strictLocalize({
        defaultMessage: "Carrier not available",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: CarrierNotAvailable(strictLocalize),
      articles: [articles.FulfillingOrders.LABEL_ISSUES],
      level: 4,
    },
    BrandResponsibility: {
      id: "BrandResponsibility",
      label: strictLocalize({
        defaultMessage: "Brand’s responsibility",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: BrandResponsibility(strictLocalize),
      level: 4,
    },
    Commercial: {
      id: "Commercial",
      label: strictLocalize({
        defaultMessage: "Commercial invoices and documentation",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FulfillingOrders.COMMERCIAL_INVOICE],
      level: 4,
    },
    Customs: {
      id: "Customs",
      label: strictLocalize({
        defaultMessage: "Contact the retailer about customs",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: Customs(strictLocalize),
      level: 4,
    },
    RefusedPackage: {
      id: "RefusedPackage",
      label: strictLocalize({
        defaultMessage: "Retailer refused package at customs",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: GeneralContactRetailerIA(strictLocalize),
      level: 4,
    },
    ShippingUKandEU: {
      id: "ShippingUKandEU",
      label: strictLocalize({
        defaultMessage: "Shipping into UK and EU",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 4,
    },
    ShippingCAN: {
      id: "ShippingCAN",
      label: strictLocalize({
        defaultMessage: "Shipping into Canada",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 4,
    },
    ShippingUS: {
      id: "ShippingUS",
      label: strictLocalize({
        defaultMessage: "Shipping into US",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 4,
    },
    OtherInternationalOrdersSOYO: {
      id: "OtherInternationalOrdersSOYO",
      label: OTHER_NODES_LABEL(strictLocalize),
      level: 4,
    },
    OtherInternationalOrdersSWF: {
      id: "OtherInternationalOrdersSWF",
      label: OTHER_NODES_LABEL(strictLocalize),
      level: 4,
    },
    AddOrUpdate: {
      id: "AddOrUpdate",
      label: strictLocalize({
        defaultMessage: "Add/Update",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: AddOrUpdate(strictLocalize),
      articles: [
        articles.FulfillingOrders.SHIP_ON_YOUR_OWN,
        articles.FulfillingOrders.EDIT_RETAILER_ORDER,
      ],
      level: 4,
    },
    OtherShipmentTrackingSOYO: {
      id: "OtherShipmentTrackingSOYO",
      label: OTHER_NODES_LABEL(strictLocalize),
      level: 4,
    },
    OtherBrandShippingSWFL4: {
      id: "OtherBrandShippingSWFL4",
      label: strictLocalize({
        defaultMessage: "Other",
        description: {
          text: "A forth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FulfillingOrders.SHIP_WITH_FAIRE],
      level: 4,
    },
  };
};
